import { Injectable } from '@angular/core';
import { environment } from '../../enviroments/environment';
import { TelemetryService } from './telemetry.service';
import { WindowService } from './window.service';

type VirtualPageProps = Record<string, string | number | boolean | null | undefined>;

interface FormStepVirtualPageContext {
  stepIndex: number;
  stepId: string;
  stepTitle: string;
  stepType: string;
}

interface ThankYouVirtualPageContext {
  thankYouProvider: string;
}

@Injectable({
  providedIn: 'root'
})
export class VirtualPageViewService {
  // Telemetry VP: separated virtual-page context so page views stay independent from custom events.
  private readonly commonContext: VirtualPageProps = {};

  constructor(
    private telemetryService: TelemetryService,
    private windowService: WindowService
  ) {}

  setCommonContext(context: VirtualPageProps): void {
    Object.assign(this.commonContext, context);
  }

  trackFormStepView(context: FormStepVirtualPageContext): void {
    if (!this.isEnabled() || !environment.telemetry?.virtualPageViews?.formStepsEnabled) {
      return;
    }

    const sequence = context.stepIndex + 1;
    const stepSlug = this.slugify(context.stepTitle) || `step-${context.stepIndex}`;
    const stepTitle = context.stepTitle || `Step ${sequence}`;
    const pageName = this.buildPageName(stepTitle, sequence);
    const uri = this.buildVirtualUri(stepSlug);

    this.telemetryService.trackPageView(pageName, uri, {
      ...this.commonContext,
      virtualPageType: 'form_step',
      journeyStage: context.stepIndex === 0 ? 'form_start' : 'form_step',
      pageSequence: context.stepIndex + 1,
      landingHost: this.getLandingHost(),
      stepIndex: context.stepIndex,
      stepId: context.stepId,
      stepTitle: context.stepTitle,
      stepSlug,
      stepType: context.stepType,
    });
  }

  trackThankYouView(context: ThankYouVirtualPageContext): void {
    if (!this.isEnabled() || !environment.telemetry?.virtualPageViews?.thankYouEnabled) {
      return;
    }

    const stepTitle = 'Incremental Monetization Thank You';
    const stepSlug = this.slugify(stepTitle);
    const pageName = this.buildPageName(stepTitle, 999);
    const uri = this.buildVirtualUri(stepSlug);

    this.telemetryService.trackPageView(pageName, uri, {
      ...this.commonContext,
      virtualPageType: 'thank_you',
      journeyStage: 'incremental_monetization_thank_you',
      pageSequence: 'final',
      landingHost: this.getLandingHost(),
      stepTitle,
      stepSlug,
      thankYouProvider: context.thankYouProvider,
    });
  }

  private isEnabled(): boolean {
    return Boolean(environment.telemetry?.virtualPageViews?.enabled && this.windowService.window);
  }

  private buildPageName(stepTitle: string, sequence?: number): string {
    const titleMain = String(this.commonContext['titleMain'] || '').trim();
    const sequencePrefix = this.formatSequence(sequence);
    const safeStepTitle = String(stepTitle || '').trim() || 'Step';
    const flowLabel = titleMain || 'Landing Form';
    return `${flowLabel} - ${sequencePrefix} - ${safeStepTitle}`;
  }

  private formatSequence(sequence?: number): string {
    if (!Number.isFinite(sequence)) {
      return '000';
    }

    const normalized = Math.max(0, Math.trunc(Number(sequence)));
    return String(normalized).padStart(3, '0');
  }

  private buildVirtualUri(stepSlug: string): string {
    const host = this.slugifyHost(this.getLandingHost());
    const landingPageId = this.slugifySegment(String(this.commonContext['landingPageId'] || 'default'));
    return `/landing/${host}/${landingPageId}/form/${stepSlug}`;
  }

  private getLandingHost(): string {
    return this.windowService.window?.location?.host || 'unknown-host';
  }

  private slugify(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private slugifySegment(value: string): string {
    return String(value || '')
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'default';
  }

  private slugifyHost(value: string): string {
    return this.slugifySegment(value.toLowerCase());
  }
}
